import React, { useEffect, useState } from 'react';
import { Config } from 'olympe'
import { DataGridPro, GridToolbar, useGridApiRef, LicenseInfo } from '@mui/x-data-grid-pro';
import { Button, CircularProgress, List, ListItem, ListItemText, Box, Tab, Tabs } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ThemeProvider } from '@mui/material/styles';
import { RCMonitorDetails } from './RCMonitorDetails.jsx';
import { getSingleCrisis, updateCrisis } from './RiskCrisis';
import { AddMaterialsPopup } from './AddMaterialsPopup.jsx';
import { Add, Delete } from '@mui/icons-material';
import { _theme } from '../shared/_theme';
import { RemoveMaterialsPopup } from './RemoveMaterialsPopup.jsx';
import GroupIcon from '@mui/icons-material/Group';
import { Collab } from './Collab.jsx'
export const RCMonitorGridAndDetails = (props) => {
    const { crisisId, throwError, getCrisisList, setCrisisList, setFilteredCrisisList, setCheckedState, setHasLoaded, crisisInfo } = props;
    const licenseKey = Config.getParameter('uiAppValues.datagrid_license')
    LicenseInfo.setLicenseKey(licenseKey)

    let columns = []
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(15);
    const [loading, setLoading] = useState(false);
    const [loadingGrid, setLoadingGrid] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0)
    const [currentCrisisDetails, setCurrentCrisisDetails] = useState(undefined);
    const [openAddMaterials, setOpenAddMaterials] = useState(false);
    const [openRemoveMaterials, setOpenRemoveMaterials] = useState(false);
    const apiRef = useGridApiRef();
    //state to manage tab index
    const [tabIndexValue, setTabIndexValue] = useState(0);

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const refreshDetails = async (loadingGridOnly = false, expectedRowCount = rowCount > 0 ? rowCount : pageSize) => {
        if (loadingGridOnly) {
            setLoadingGrid(true);
        } else {
            setLoading(true);
        }

        const data = await getSingleCrisis(crisisId, pageSize, page, expectedRowCount);

        if (data.error) {
            throwError(null, data.message);
        } else if (data.crisisMaterials.length < 1) {
            throwError(null, 'Empty response received');
        } else {
            setCurrentCrisisDetails(data.crisisDetails);
            setRows(data.crisisMaterials);
            setRowCount(data.count);
        }

        setLoading(false);
        setLoadingGrid(false);
    };

    const refreshAll = async () => {
        setLoading(true);
        await getCrisisList()
            .then((data) => {
                if (!data.error) {
                    setFilteredCrisisList(data);
                    setCrisisList(data);
                    setCheckedState(new Array(data.length).fill(false));
                }
            });
        await refreshDetails();
    };

    const handleSaveClick = async (id, details) => {
        setLoading(true)
        await updateCrisis(id, details)
        await refreshAll();
    }
    //function used to change the tab index values like 0 for Materials,1 for Collaborators
    const handleTabChange = (event, tabNumberValue) => {
        setTabIndexValue(tabNumberValue);
        if (tabNumberValue === 1) {

            if (props.crisisInfo.groupID !== null) {
                props.context.set("groupID", props.crisisInfo.groupID);
                props.context.trigger('Fetch members list');
            }
        }
    }
    //variable used to style the active tab like Materials and Collaborators
    const tabStyle = {
        '&.Mui-selected': {
            color: '#808080',
            fontWeight: '800'
        }, '&:focus': {
            outline: 'none',
            color: '#808080',
            fontWeight: '800'
        },
    }

    // fetch crisis data on first render, and update grid materials only on page change
    useEffect(() => {
        refreshDetails(currentCrisisDetails !== undefined);
    }, [page, pageSize]);

    if (rows && rows.length > 0) {

        columns = [
            {
                field: 'plant',
                headerName: 'Plant',
                minWidth: 200,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.plantId || ''} - ${params.row.plantDescription || ''}`,
                renderCell: (params) => {
                    return <ListItemText primary={params.row.plantId} secondary={params.row.plantDescription} />;
                },
            },
            {
                field: 'material',
                headerName: 'Material',
                minWidth: 300,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.materialId || ''} - ${params.row.materialDescription || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={parseInt(params.row.materialId)}
                            secondary={params.row.materialDescription}
                        />
                    );
                },
            },
            {
                field: 'specs',
                headerName: 'Specification',
                minWidth: 300,
                resizable: true,
                pinnable: false,
                valueGetter: (params) =>
                    `${params.row.specificationCode || ''} - ${params.row.specificationDescription || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row.specificationCode}
                            secondary={params.row.specificationDescription}
                        />
                    );
                },
            },
            {
                field: 'vendor',
                headerName: 'Main Vendor',
                minWidth: 200,
                resizable: true,
                pinnable: false,

                valueGetter: (params) =>
                    params.row.mainVendor && params.row.mainVendor[0] ?
                        `${params.row.mainVendor[0]['id']} - ${params.row.mainVendor[0]['description']}` :
                        '',
                renderCell: (params) => (
                    <List dense={true}>
                        {params.row.mainVendor.map((vendor) => {
                            return (
                                <ListItem key={vendor.id + vendor.description} style={{ paddingTop: '1px' }}>
                                    <ListItemText primary={vendor.id} secondary={vendor.description} />
                                </ListItem>
                            );
                        })}
                    </List>
                ),
            },
            {
                field: 'vendorSite',
                headerName: 'Vendor Site',
                minWidth: 250,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.vendorSiteNumber || ''} - ${params.row.vendorSiteDescription || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={parseInt(params.row.vendorSiteNumber)}
                            secondary={params.row.vendorSiteDescription}
                        />
                    );
                },
            },
            {
                field: 'vendorSiteCountry',
                headerName: 'Vendor site Country',
                minWidth: 150,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.vendorSiteCountry || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row.vendorSiteCountry}
                        />
                    );
                },
            },
            {
                field: 'financialRating',
                headerName: 'Financial rating',
                minWidth: 150,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.financialRating || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row.financialRating}
                        />
                    );
                },
            },
            {
                field: 'reasonCode',
                headerName: 'BCP status',
                minWidth: 300,
                resizable: true,
                pinnable: false,
                valueGetter: (params) => `${params.row.reasonCode || ''}`,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row.reasonCode}
                        />
                    );
                },
            },
        ]
    }

    const useStyles = makeStyles (
        () => {
            return {
                root: {
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        height: '100%',
                    },
                    '& .MuiPaginationItem-root': {
                        borderRadius: 0,
                    },
                    '& .MuiDataGrid-row': {
                        backgroundColor: '#FEF4EB',
                        borderRadius: '7px',
                        marginBottom: '6px',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                        overflow: 'auto',
                    },
                    '& .wrapHeader .MuiDataGrid-colCellTitle': {
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        'padding-bottom': '80px !important',
                    },
                    // disable select all (as deleting all materials in a crisis delete the crisis)
                    // '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                    //     display: 'none',
                    // },
                },
            }
        }
    )

    const classes = useStyles()

    return (
        <div style={{ display: 'flex', height: '100%', width: '100%', minHeight: '600px' }} className="gridContainer">
            {loading || !currentCrisisDetails || !rows ?
                <CircularProgress style={{ width: '3%', height: '3%', margin: 'auto' }} /> :
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexGrow: 1, height: '100%', overflow: 'auto !important', width: '100%' }} className={classes.root}>
                    <div style={{ width: '80%', position: 'relative' /* force this div to have "positioned" state, so that buttons can use absolute positioning */ }}>
                        <ThemeProvider theme={_theme}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                {/*Changing the tabs it will call a handlechange function*/}
                                <Tabs value={tabIndexValue} onChange={handleTabChange} aria-label="basic tabs example">
                                    <Tab label="Materials" style={{ textTransform: 'capitalize' }} sx={tabStyle} />
                                    <Tab label="Collaborators" iconPosition="end" style={{ textTransform: 'capitalize' }} icon={<GroupIcon />} sx={tabStyle} />
                                </Tabs>
                            </Box>
                            {/*Render Tab content based on active tab index value,this will render Data Grid in materials tab  */}
                            {tabIndexValue === 0 && (<Box>
                                <DataGridPro
                                    pagination={true}
                                    style={{ minHeight: '600px' }}
                                    rowHeight={70}
                                    headerHeight={70}
                                    page={page}
                                    pageSize={pageSize}
                                    rowCount={rowCount}
                                    rowsPerPageOptions={[15, 50, 100]}
                                    onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                                    onPageChange={handlePageChange}
                                    loading={loadingGrid}
                                    rows={rows}
                                    columns={columns}
                                    density="compact"
                                    paginationMode="server"
                                    disableColumnMenu={true}
                                    disableColumnFilter
                                    checkboxSelection
                                    getRowId={(row) => row.recordSequence}
                                    components={{
                                        Toolbar: GridToolbar,
                                    }}
                                    disableDensitySelector={true}
                                    disableColumnSelector={true}
                                    apiRef={apiRef}
                                />
                                <Button
                                    variant='contained'
                                    color='primary'
                                    id={`add-materials-${crisisId}`}
                                    startIcon={<Add />}
                                    style={{ position: 'absolute', bottom: '65px', right: '20px', textTransform: 'none' }}
                                    onClick={() => { setOpenAddMaterials(true); }}
                                >
                                    Add
                            </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    id={`remove-materials-${crisisId}`}
                                    style={{ position: 'absolute', bottom: '65px', right: '110px', textTransform: 'none' }}
                                    startIcon={<Delete />}
                                    onClick={(event) => {
                                        setOpenRemoveMaterials(true);
                                    }}
                                >
                                    Remove selected
                            </Button>
                            </Box>)}

                            {/*Render Tab content based on active tab index value,this content will render in Collaborators tab  */}
                            {tabIndexValue === 1 && (<Box sx={{ p: 2 }}>
                                <Collab context={props.context} groupID={props.crisisInfo.groupID} />
                            </Box>)}
                        </ThemeProvider>
                    </div>
                    <RCMonitorDetails
                        currentCrisisDetails={currentCrisisDetails}
                        crisisId={crisisId}
                        handleSaveClick={handleSaveClick}
                    />
                </div>
            }
            {openAddMaterials && <AddMaterialsPopup
                crisisDetails={currentCrisisDetails}
                onClose={() => {
                    setOpenAddMaterials(false);
                }}
                handleUpdate={(addedRowsCount) => {
                    refreshDetails(true, rowCount + addedRowsCount);
                }}
                throwError={throwError}
                context={props.context}
            />}
            {openRemoveMaterials && <RemoveMaterialsPopup
                crisisDetails={currentCrisisDetails}
                materialsToDelete={Array.from(apiRef.current.getSelectedRows().values())}
                totalRowCount={rowCount}
                handleUpdate={(removedRowsCount) => {
                    // avoid api error when deleted all elements on the last page of the grid
                    //  => trigger navigation to the previous page
                    const expectedRowCount = rowCount - removedRowsCount;
                    if (expectedRowCount / pageSize <= page) {
                        setRowCount(expectedRowCount);
                        setPage(page - 1);
                    } else {
                        refreshDetails(true, expectedRowCount);
                    }
                }}
                onClose={() => {
                    setOpenRemoveMaterials(false);
                }}
                throwError={throwError}
            />}
        </div>
    )
}